import { Flex, FlexProps, useDisclosure } from '@chakra-ui/react';
import { ReactComponent as RotateIcon } from 'assets/icons/rotate_circle.svg';
import { DESIGN_GENERATED, DESIGN_GENERATED_FAIl, DESIGN_GENERATED_SUCCESS } from 'constants/mixpanel';
import { checkUserOverLimit, saveGeneratedDesign } from 'libs/e1api/infographic';
import { uploadCSV } from 'libs/e1api/uploadCSV';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { DesignPreview } from 'modules/DesignGenerator/components/DesignPreview/DesignPreview';
import { newStore } from 'modules/Editor/store';
import { useUser } from 'modules/User';
import { ChatInput, ChatPanel } from 'modules/common/components/Chat';
import { ChatData, MessageType, PanelActionCallback } from 'modules/common/components/Chat/Chat.types';
import { FileTag } from 'modules/common/components/FileTag';
import SpreadSheetImport from 'modules/common/components/SpreadSheetImport';
import { useToast } from 'modules/common/components/Toast';
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Provider } from 'react-redux';
import { InfographState } from 'types/infographTypes';
import { TableData } from 'types/spreadsheet';
import { shuffle } from 'utils/array';
import { isDevelopment, isProductionDomain } from 'utils/environment';
import { convertToCSV } from 'utils/sheet';

import { DebugPanel } from './DebugPanel/DebugPanel';
import { categoryGenMessageMap, categoryGenerationTypeMap, categoryTemplatesListMap } from './DesignAI.config';
import { DESIGN_AI_CHAT_CONFIG } from './DesignAI.config';
import { generateDesignByAPI, getDefaultChatData, parseCommandToObject, sanitizeTableData } from './DesignAI.helpers';
import { ChatErrorTypes, CommandType, DesignTemplateData } from './DesignAI.types';
import { DesignAICategories, useDesignAIContext } from './DesignAIContext';

interface ChatContainerProps extends FlexProps {}

/**
 * Handle the chat messages
 */
export const ChatContainer = memo((inProps: ChatContainerProps) => {
  const { category, setCategory, lastFocusableElementRef } = useDesignAIContext();
  const { t: tDesignGenerator } = useTranslation('design_generator', { useSuspense: false });
  const { t } = useTranslation('common_components', { useSuspense: false, keyPrefix: 'chat' });
  const [chatDataList, setChatDataList] = useState<ChatData[]>([]);
  const [isDisabled, setIsDisabled] = useState(false);
  const [templateIndex, setTemplateIndex] = useState(0);

  const categoryName = tDesignGenerator(`designAI.categories.${category}`);
  const inputPlaceholder = t('promptInput.placeholder', { categoryName });
  const shuffledTemplateIdList = useMemo<DesignTemplateData[]>(
    () => shuffle(categoryTemplatesListMap[category]),
    [category],
  );
  const generatedInfographStateCacheRef = useRef<{ [e2DesignId: string]: InfographState }>({});
  const { isOpen: isDebugPanelOpen, onOpen: onDebugPanelOpen, onClose: onDebugPanelClose } = useDisclosure();
  const { data: userInfo } = useUser();

  const firstRender = useRef(true);

  const tableDataRef = useRef<{ tableData: TableData; fileType: string; fileName: string } | undefined>();

  // Eventually, we will deprecated the /chart-generator route and use only the infograph route
  // For now, we need this url to specify that always generate the chart design
  if (window.location.pathname === '/chart-generator') {
    setCategory(DesignAICategories.Charts);
  }

  // Error toast
  const toast = useToast();
  const showError = useCallback(
    (message: string) =>
      toast({
        title: message,
        status: 'error',
      }),
    [toast],
  );

  const validateFreeDesignLimit = useCallback(async () => {
    const res = await checkUserOverLimit();
    return res.status === 'ok' && res.overlimit === false;
  }, []);

  // Error Handler to display the chat message or toast
  const handleError = useCallback(
    (errorMessage: ChatErrorTypes) => {
      switch (errorMessage) {
        case ChatErrorTypes.USER_OVERLIMIT:
          setChatDataList((prev) => {
            return [
              ...prev,
              {
                type: MessageType.BOT,
                messages: ['You have reached your design limit. You can upgrade to create more designs.'],
                createdAt: Date.now(),
              },
            ];
          });
          setIsDisabled(true);
          break;
        case ChatErrorTypes.USER_OVERLIMIT_TOAST:
          showError(
            'You have reached the maximum number of designs on your account. Please delete some designs and try again.',
          );
          break;
        case ChatErrorTypes.BAD_REQUEST:
        case ChatErrorTypes.INTERNAL_SERVER_ERROR:
        default:
          if (!isProductionDomain) {
            console.error(errorMessage);
          }

          setChatDataList((prev) => {
            const lastUserMessage = prev.filter((data) => data.type === MessageType.USER).pop();
            let result = [
              ...prev,
              {
                type: MessageType.BOT,
                messages: ['Something went wrong while generating your design. Please try again!'],
                createdAt: Date.now(),
              },
            ];

            if (lastUserMessage) {
              result.push({
                type: MessageType.BUTTON,
                messages: ['retry', tDesignGenerator('messages.button.retry'), 'end', lastUserMessage.messages[0]],
                children: <RotateIcon />,
                createdAt: Date.now(),
              });
            }
            // Stop generating messages
            return result.map((data) => {
              if (data.type === MessageType.BOT_GENERATING) data.messages[0] = 'stop';
              return data;
            });
          });
          setIsDisabled(false);
          break;
      }
    },
    [tDesignGenerator, showError],
  );

  // Trigger when the when the user submit a message from the chat input
  const handleSubmit = useCallback(
    async (userMessage: string, mixpanelActionType: string = DESIGN_GENERATED.actionTypeCategoryName.Generate) => {
      // Handle the debug command
      const command = parseCommandToObject(userMessage);
      const isVenngageAccount = userInfo && /@venngage\.com$/.test(userInfo.email);
      if (isVenngageAccount && command && command.type === CommandType.DebugPanel && command.command === 'on') {
        onDebugPanelOpen();
        return;
      }

      setIsDisabled(true);
      let { e2DesignId, e2DesignTitle } = shuffledTemplateIdList[templateIndex];

      console.log(`INFO: Generating Design using e2DesignId ${e2DesignId} with its title: ${e2DesignTitle}`);

      // Track the response itme of the API
      const startTime = performance.now();

      try {
        // Remove all suggestion message when it trigger handle submit
        const messages = tableDataRef.current ? [`${userMessage} `] : [userMessage];
        const children = tableDataRef.current ? <FileTag name={tableDataRef.current.fileName} /> : undefined;
        setChatDataList((prev) => [
          ...prev.filter((item) => item.type !== MessageType.SUGGESTION), // Remove the topic suggestion
          { type: MessageType.USER, messages: messages, createdAt: Date.now(), children },
        ]);

        // Check the free user design limitation
        const isValid = await validateFreeDesignLimit();
        if (!isValid) throw new Error(ChatErrorTypes.USER_OVERLIMIT);

        // Add the generating message
        setChatDataList((prev) => [
          ...prev,
          {
            type: MessageType.BOT_GENERATING,
            messages: ['start', categoryGenMessageMap[category]],
            createdAt: Date.now(),
          },
        ]);

        // Mixpanel event
        Mixpanel.track(DESIGN_GENERATED.eventName, {
          template_id: e2DesignId,
          template_title: e2DesignTitle,
          template_category: category,
          user_input: userMessage,
          action_type: mixpanelActionType,
        });

        // Generating user design
        const generationType = categoryGenerationTypeMap[category];
        const newInfographState = await generateDesignByAPI({
          e2DesignId,
          userContext: userMessage,
          generationType,
          tableData: tableDataRef.current?.tableData,
        });
        generatedInfographStateCacheRef.current[e2DesignId] = newInfographState;

        setChatDataList((prev) => {
          return [
            ...prev.map((data) => {
              if (data.type === MessageType.BOT_GENERATING) data.messages[0] = 'stop';
              return data;
            }),
            {
              type: MessageType.DESIGN,
              messages: [e2DesignId, userMessage],
              children: (
                <Provider store={newStore()}>
                  <DesignPreview fitWidth={true} infographState={newInfographState} />
                </Provider>
              ),
              createdAt: Date.now(),
            },
            {
              type: MessageType.BUTTON,
              messages: ['regenerate', tDesignGenerator('messages.button.regenerate'), 'end', userMessage],
              children: <RotateIcon />,
              createdAt: Date.now(),
            },
          ];
        });

        // Mixpanel event
        const endTime = performance.now();
        const durationInSeconds = (endTime - startTime) / 1000;
        Mixpanel.track(DESIGN_GENERATED_SUCCESS.eventName, {
          template_id: e2DesignId,
          template_title: e2DesignTitle,
          template_category: category,
          user_input: userMessage,
          action_type: mixpanelActionType,
          response_time: durationInSeconds,
        });

        setIsDisabled(false);
        setTemplateIndex((prev) => (prev + 1 >= shuffledTemplateIdList.length ? 0 : prev + 1));
      } catch (err) {
        const error = err as Error; // type assertion
        if (error?.message) {
          // Mixpanel event
          const endTime = performance.now();
          const durationInSeconds = Math.round(endTime - startTime) / 1000;
          Mixpanel.track(DESIGN_GENERATED_FAIl.eventName, {
            template_id: e2DesignId,
            template_title: e2DesignTitle,
            template_category: category,
            user_input: userMessage,
            action_type: mixpanelActionType,
            response_time: durationInSeconds,
          });

          handleError(error.message as ChatErrorTypes);
        }
      }
    },
    [
      tDesignGenerator,
      userInfo,
      templateIndex,
      shuffledTemplateIdList,
      onDebugPanelOpen,
      validateFreeDesignLimit,
      handleError,
      category,
    ],
  );

  /**
   * Will duplicate the design and open the editor
   */
  const handleEditDesign = useCallback(
    async (e2DesignId: string, userMessage: string) => {
      setIsDisabled(true);
      try {
        const sourceId = shuffledTemplateIdList.find((data) => data.e2DesignId === e2DesignId)?.e1TemplateId;
        const generatedInfographState = generatedInfographStateCacheRef?.current?.[e2DesignId];

        if (!sourceId || !generatedInfographState) {
          if (!isProductionDomain) {
            console.error(`ERROR: Invalid source id: ${sourceId}, or generated infograph state.`);
          }
          throw new Error(ChatErrorTypes.INVALID_INFOGRAPHIC_STATE);
        }

        // Save design to user's account
        const response = await saveGeneratedDesign(generatedInfographState, sourceId);

        // Validate the free user limit of 5 designs
        if (response.status === 'overlimit') throw new Error(ChatErrorTypes.USER_OVERLIMIT_TOAST);

        if (response.status === 'ok' && response.designId) {
          // Open the new tab
          const link = `/design/${response.designId}`;
          const newTab = window.open(link, '_blank');

          // If open the new tab is blocked in a specific browser(Safari, Brave and etc), redirect from the current page
          if (!newTab) {
            window.location.href = link;
          }
        }

        throw new Error(ChatErrorTypes.INTERNAL_SERVER_ERROR);
      } catch (err) {
        const error = err as Error;
        if (error?.message) {
          handleError(error.message as ChatErrorTypes);
        }
      } finally {
        setIsDisabled(false);
      }
    },
    [shuffledTemplateIdList, handleError],
  );

  // Handle all events triggered by the message component
  const handleUpdate = useCallback<PanelActionCallback>(
    (type, payload, chatData) => {
      switch (type) {
        case MessageType.SUGGESTION:
          handleSubmit(payload, DESIGN_GENERATED.actionTypeCategoryName.Generate);
          break;

        case MessageType.DESIGN:
          if (payload === 'click') {
            handleEditDesign(chatData.messages[0], chatData.messages[1]);
          }
          break;
        case MessageType.BUTTON:
          if (['regenerate', 'retry'].includes(payload)) {
            setChatDataList((prev) => [...prev.filter((item) => item !== chatData)]);
            handleSubmit(
              chatData.messages[3],
              payload === 'regenerate'
                ? DESIGN_GENERATED.actionTypeCategoryName.Regenerate
                : DESIGN_GENERATED.actionTypeCategoryName.Retry,
            );
          }
          break;

        default:
          break;
      }
    },
    [handleEditDesign, handleSubmit],
  );

  const onImportData = useCallback(
    (tableData: TableData, fileType: string, uploadedFileName: string) => {
      uploadCSV(convertToCSV(tableData))
        .then((res) => {
          const { fileName } = res;

          if (!isProductionDomain) {
            console.info(`Upload the csv file sucessfully; FileName: ${fileName}`);
          }

          tableDataRef.current = {
            tableData: sanitizeTableData(tableData),
            fileName: uploadedFileName,
            fileType,
          };

          setCategory(DesignAICategories.Charts);
        })
        .catch((err) => {
          if (isDevelopment) {
            throw err;
          }

          if (!isProductionDomain) {
            console.error(err);
          }
        });
    },
    [setCategory],
  );

  const onClearUpload = useCallback(() => {
    tableDataRef.current = undefined;
    setCategory(DesignAICategories.Infographics);
  }, [setCategory]);

  // Validate user for the first render
  // It is not using the useEffect to avoid the StrictMode issue
  if (firstRender.current) {
    firstRender.current = false;

    (async () => {
      const isValid = await validateFreeDesignLimit();
      if (isValid) {
        setChatDataList(getDefaultChatData(category));
      } else {
        handleError(ChatErrorTypes.USER_OVERLIMIT);
      }
    })();
  }

  return (
    <Flex direction={'column'} h='full' data-testid='chat' {...inProps}>
      <ChatPanel
        isDisabled={isDisabled}
        messages={chatDataList}
        config={DESIGN_AI_CHAT_CONFIG}
        autoScrollToBottom={isDisabled}
        onUpdate={handleUpdate}
      />
      <ChatInput
        submitButtonRef={lastFocusableElementRef}
        onUpload={onImportData}
        ImportComponent={SpreadSheetImport}
        onClearUpload={onClearUpload}
        onSubmit={handleSubmit}
        isDisabled={isDisabled}
        placeholder={inputPlaceholder}
      />
      <DebugPanel
        isOpen={isDebugPanelOpen}
        onClose={onDebugPanelClose}
        chatData={chatDataList}
        setChatData={setChatDataList}
      />
    </Flex>
  );
});

ChatContainer.displayName = 'ChatContainer';
